package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.gymly.audit.TrackAdminAudit
import org.gymly.data.AdminRepo
import org.gymly.esAndroid
import org.gymly.i18n.ActiveLocale
import org.gymly.i18n.GymlyLocale
import org.gymly.i18n.LocalStrings
import org.gymly.i18n.stringsFor
import org.gymly.model.AdminNotificationItem
import org.gymly.model.AdminSettingsPermissionModule
import org.gymly.model.AdminRoute
import org.gymly.model.AdminSearchHit
import org.gymly.model.AdminUser
import org.gymly.model.allowedReports
import org.gymly.model.canApproveAnything
import org.gymly.model.canOpenRoute
import org.gymly.model.canOpenSettings
import org.gymly.navigation.WebHistory
import org.gymly.navigation.rememberWebHistoryStateWithInitialReplace
import org.gymly.theme.AdminColors

@Composable
fun AdminApp(
    user: AdminUser,
    onLogout: () -> Unit,
) {
    var currentUser by remember(user.id) { mutableStateOf(user) }
    LaunchedEffect(currentUser.preferredLocale) {
        AdminRepo.session.preferredLocale = currentUser.preferredLocale
        ActiveLocale.setFromPreferred(currentUser.preferredLocale)
    }
    val (route, setRoute) = rememberWebHistoryStateWithInitialReplace(
        initial = if (user.role.equals("PLATFORM", ignoreCase = true)) AdminRoute.Settings else AdminRoute.Dashboard,
        hashFor = { WebHistory.adminHash(it) },
        parseFromHash = { WebHistory.parseAdminRoute(it) },
    )
    var unread by remember { mutableStateOf(0L) }
    var notificationItems by remember { mutableStateOf<List<AdminNotificationItem>>(emptyList()) }
    var pendingApprovals by remember { mutableStateOf(0L) }
    var openSettingsApprovals by remember { mutableStateOf(false) }
    var pendingApprovalPopup by remember { mutableStateOf<String?>(null) }
    var photoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var mobileMenuOpen by remember { mutableStateOf(false) }
    var clientAccountsEnabled by remember { mutableStateOf(false) }
    val notifScope = rememberCoroutineScope()
    val pendingApprovalNotifier = remember {
        PendingApprovalNotifier { msg -> pendingApprovalPopup = msg }
    }

    // Aprobaciones ya vistas: una nueva resolución sube el contador y las pantallas de gestión recargan.
    var dataRevision by remember { mutableStateOf(0) }
    var knownResolvedIds by remember { mutableStateOf<Set<String>?>(null) }

    suspend fun refreshNotifications() {
        runCatching { AdminRepo.api.notifications() }
            .onSuccess { feed ->
                unread = feed.unreadCount
                notificationItems = feed.items
                val resolved = feed.items.filter { it.isApprovalResolved() }.map { it.id }.toSet()
                val known = knownResolvedIds
                if (known != null && (resolved - known).isNotEmpty()) dataRevision++
                knownResolvedIds = resolved
            }
    }

    LaunchedEffect(pendingApprovalPopup) {
        if (pendingApprovalPopup != null) {
            refreshNotifications()
            if (currentUser.permissionsKnown && currentUser.canApproveAnything()) {
                pendingApprovals = runCatching { AdminRepo.api.pendingApprovalsCount() }.getOrDefault(pendingApprovals)
            }
        }
    }

    LaunchedEffect(Unit) {
        refreshNotifications()
        clientAccountsEnabled = runCatching {
            AdminRepo.api.getSettingsModules().clientAccountsEnabled
        }.getOrDefault(false)
        try {
            val me = AdminRepo.api.fetchMe()
            // No pisar un idioma que el usuario ya cambió en esta sesión (p. ej. mientras cargaba /me).
            val localeToKeep = currentUser.preferredLocale
                ?.takeIf { it != user.preferredLocale }
                ?: me.preferredLocale
            currentUser = AdminUser(
                id = me.id,
                displayName = me.displayName,
                email = me.email,
                role = me.role,
                photoUrl = me.photoUrl,
                preferredLocale = localeToKeep,
                membershipOverride = me.membershipOverride ||
                    me.role.equals("ADMIN", ignoreCase = true) ||
                    me.role.equals("SUPERVISOR", ignoreCase = true) ||
                    user.membershipOverride,
                permissions = me.permissions,
                permissionsKnown = true,
            )
            AdminRepo.session.preferredLocale = localeToKeep
            ActiveLocale.setFromPreferred(localeToKeep)
        } catch (_: Exception) {
            // keep login snapshot
        }
    }

    LaunchedEffect(currentUser.photoUrl, currentUser.photoRevision) {
        if (currentUser.photoUrl.isNullOrBlank()) {
            if (currentUser.photoRevision == 0) photoBytes = null
            return@LaunchedEffect
        }
        val fetched = runCatching { AdminRepo.api.fetchMyPhotoBytes() }.getOrNull()
        if (fetched != null && fetched.isNotEmpty()) {
            photoBytes = fetched
        }
    }

    fun navigate(to: AdminRoute) {
        if (to != AdminRoute.Settings) openSettingsApprovals = false
        setRoute(to)
        mobileMenuOpen = false
    }

    val showReports = currentUser.permissionsKnown && currentUser.allowedReports().isNotEmpty()
    val showSettings = !currentUser.permissionsKnown || currentUser.canOpenSettings()
    // Monitoreo de términos: solo si Empresa lo tiene activo y la licencia incluye portal o app.
    var termsMonitoringVisible by remember { mutableStateOf(false) }
    LaunchedEffect(route) {
        termsMonitoringVisible = runCatching { AdminRepo.api.getCompany() }.getOrNull()
            ?.let { it.termsMonitoringEnabled && it.termsMonitoringAvailable } ?: false
    }
    LaunchedEffect(route) {
        // Refrescar flag al navegar (p. ej. tras cambiarlo en Empresa).
        clientAccountsEnabled = runCatching {
            AdminRepo.api.getSettingsModules().clientAccountsEnabled
        }.getOrDefault(clientAccountsEnabled)
    }

    LaunchedEffect(route, currentUser.permissionsKnown, currentUser.permissions, clientAccountsEnabled, currentUser.role) {
        if (currentUser.role.equals("PLATFORM", ignoreCase = true) && route != AdminRoute.Settings && route != AdminRoute.Profile) {
            navigate(AdminRoute.Settings)
        } else if (currentUser.permissionsKnown && !currentUser.canOpenRoute(route)) {
            navigate(if (currentUser.role.equals("PLATFORM", ignoreCase = true)) AdminRoute.Settings else AdminRoute.Dashboard)
        } else if (route == AdminRoute.ClientAccounts && !clientAccountsEnabled) {
            navigate(AdminRoute.Dashboard)
        }
    }

    LaunchedEffect(route, currentUser.permissions) {
        if (!currentUser.permissionsKnown) return@LaunchedEffect
        if (!currentUser.canApproveAnything()) {
            pendingApprovals = 0L
            return@LaunchedEffect
        }
        pendingApprovals = runCatching { AdminRepo.api.pendingApprovalsCount() }.getOrDefault(0L)
    }

    // Campana: refresco periódico para avisos de aprobación / resolución.
    LaunchedEffect(Unit) {
        while (isActive) {
            delay(5_000)
            refreshNotifications()
            if (currentUser.permissionsKnown && currentUser.canApproveAnything()) {
                pendingApprovals = runCatching { AdminRepo.api.pendingApprovalsCount() }.getOrDefault(pendingApprovals)
            }
        }
    }

    // El idioma del panel sigue la preferencia del usuario (`preferredLocale`).
    val activeStrings = stringsFor(GymlyLocale.from(currentUser.preferredLocale))
    SideEffect { ActiveLocale.current = activeStrings.locale }

    CompositionLocalProvider(
        LocalStrings provides activeStrings,
        LocalAdminDataRevision provides dataRevision,
        LocalAdminBumpDataRevision provides { dataRevision++ },
    ) {
    ProvidePendingApprovalNotifier(pendingApprovalNotifier) {
    TrackAdminAudit(route = route, onLogout = onLogout) { logout ->
        BoxWithConstraints(Modifier.fillMaxSize().background(AdminColors.Surface)) {
            pendingApprovalPopup?.let { msg ->
                PendingApprovalNeededDialog(
                    message = msg,
                    onDismiss = { pendingApprovalPopup = null },
                    onViewMyRequests = {
                        openSettingsApprovals = true
                        navigate(AdminRoute.Settings)
                    },
                )
            }
            // Altura finita del viewport — evita "infinity maximum height" en scroll (CMP Web + móvil).
            val viewportHeight = maxHeight
            val wide = maxWidth.value.toInt() >= AdminLayoutBreakpoints.SIDEBAR_WIDE_MIN
            val compactTopBar = maxWidth.value.toInt() <= AdminLayoutBreakpoints.COMPACT_MAX

            @Composable
            fun MainContent(columnModifier: Modifier) {
                Column(columnModifier) {
                    AdminTopBar(
                        userName = currentUser.displayName,
                        userEmail = currentUser.email,
                        photoUrl = null,
                        photoBytes = photoBytes,
                        unreadNotifications = unread,
                        notificationItems = notificationItems,
                        compact = compactTopBar,
                        showMenuButton = !wide,
                        onMenuClick = { mobileMenuOpen = true },
                        onLogout = logout,
                        onSearchSelect = { hit: AdminSearchHit ->
                            val target = when (hit.type) {
                                "CLIENT" -> AdminRoute.Clients
                                "STAFF" -> AdminRoute.Workers
                                "MEMBERSHIP" -> AdminRoute.Memberships
                                "PLAN" -> AdminRoute.MembershipPlans
                                "PAYMENT" -> AdminRoute.MembershipOrders
                                "BOOKING" -> AdminRoute.Bookings
                                "PRODUCT" -> AdminRoute.Products
                                else -> route
                            }
                            if (!currentUser.permissionsKnown || currentUser.canOpenRoute(target)) {
                                navigate(target)
                            }
                        },
                        onRefreshNotifications = {
                            notifScope.launch { refreshNotifications() }
                        },
                        onUnreadChange = { count ->
                            unread = count
                            notifScope.launch { refreshNotifications() }
                        },
                        onOpenApprovals = {
                            openSettingsApprovals = true
                            navigate(AdminRoute.Settings)
                        },
                    )
                    Box(
                        Modifier
                            .weight(1f)
                            .fillMaxWidth(),
                    ) {
                        AdminRouteContent(
                            route = route,
                            user = currentUser,
                            pendingApprovalsCount = pendingApprovals,
                            openSettingsApprovals = openSettingsApprovals,
                            onNavigate = ::navigate,
                            onUserUpdated = { updated, bytes ->
                                currentUser = updated
                                if (bytes != null) {
                                    photoBytes = bytes.takeIf { it.isNotEmpty() }
                                } else if (updated.photoUrl.isNullOrBlank()) {
                                    photoBytes = null
                                }
                            },
                        )
                        AsistenteFlotante(Modifier.align(Alignment.BottomEnd))
                    }
                }
            }

            if (wide) {
                Row(Modifier.fillMaxWidth().height(viewportHeight)) {
                    AdminSidebar(
                        current = route,
                        onSelect = { target ->
                            if (target == AdminRoute.Settings && pendingApprovals > 0) {
                                openSettingsApprovals = true
                            }
                            navigate(target)
                        },
                        showReports = showReports,
                        showSettings = showSettings,
                        showTermsMonitoring = termsMonitoringVisible,
                        clientAccountsEnabled = clientAccountsEnabled,
                        pendingApprovalsCount = pendingApprovals,
                        canOpen = { !currentUser.permissionsKnown || currentUser.canOpenRoute(it) },
                        modifier = Modifier.width(240.dp).fillMaxHeight(),
                    )
                    MainContent(Modifier.weight(1f).fillMaxHeight())
                }
            } else if (esAndroid()) {
                Column(Modifier.fillMaxWidth().height(viewportHeight)) {
                    MainContent(Modifier.weight(1f).fillMaxWidth())
                    AdminBarraInferior(
                        current = route,
                        canOpen = { !currentUser.permissionsKnown || currentUser.canOpenRoute(it) },
                        onSelect = { target ->
                            if (target == AdminRoute.Settings && pendingApprovals > 0) {
                                openSettingsApprovals = true
                            }
                            navigate(target)
                        },
                        onMas = { mobileMenuOpen = true },
                        masActivo = mobileMenuOpen,
                    )
                }
                if (mobileMenuOpen) {
                    AdminHojaMas(
                        current = route,
                        showReports = showReports,
                        showSettings = showSettings,
                        showTermsMonitoring = termsMonitoringVisible,
                        clientAccountsEnabled = clientAccountsEnabled,
                        pendingApprovalsCount = pendingApprovals,
                        canOpen = { !currentUser.permissionsKnown || currentUser.canOpenRoute(it) },
                        onSelect = { target ->
                            if (target == AdminRoute.Settings && pendingApprovals > 0) {
                                openSettingsApprovals = true
                            }
                            navigate(target)
                        },
                        onDismiss = { mobileMenuOpen = false },
                    )
                }
            } else {
                Box(Modifier.fillMaxWidth().height(viewportHeight)) {
                    MainContent(Modifier.fillMaxSize())
                    if (mobileMenuOpen) {
                        Box(
                            Modifier
                                .fillMaxSize()
                                .background(Color.Black.copy(alpha = 0.45f))
                                .clickable { mobileMenuOpen = false },
                        )
                        Box(
                            Modifier
                                .width(280.dp)
                                .fillMaxHeight()
                                .align(Alignment.CenterStart)
                                .clickable(onClick = {}),
                        ) {
                            AdminSidebar(
                                current = route,
                                onSelect = { target ->
                                    if (target == AdminRoute.Settings && pendingApprovals > 0) {
                                        openSettingsApprovals = true
                                    }
                                    navigate(target)
                                },
                                showReports = showReports,
                                showSettings = showSettings,
                                showTermsMonitoring = termsMonitoringVisible,
                                clientAccountsEnabled = clientAccountsEnabled,
                                pendingApprovalsCount = pendingApprovals,
                                canOpen = { !currentUser.permissionsKnown || currentUser.canOpenRoute(it) },
                                modifier = Modifier.fillMaxSize(),
                            )
                        }
                    }
                }
            }
        }
    }
    }
    }
}

@Composable
private fun AdminRouteContent(
    route: AdminRoute,
    user: AdminUser,
    pendingApprovalsCount: Long,
    openSettingsApprovals: Boolean,
    onNavigate: (AdminRoute) -> Unit,
    onUserUpdated: (AdminUser, ByteArray?) -> Unit,
) {
    CompositionLocalProvider(
        LocalAdminPermissions provides
            if (user.permissionsKnown) user.permissions.toSet() else AdminSettingsPermissionModule.entries.toSet(),
    ) {
        AdminRouteBody(route, user, pendingApprovalsCount, openSettingsApprovals, onNavigate, onUserUpdated)
    }
}

@Composable
private fun AdminRouteBody(
    route: AdminRoute,
    user: AdminUser,
    pendingApprovalsCount: Long,
    openSettingsApprovals: Boolean,
    onNavigate: (AdminRoute) -> Unit,
    onUserUpdated: (AdminUser, ByteArray?) -> Unit,
) {
    when (route) {
        AdminRoute.Dashboard -> AdminDashboardScreen(onNavigate = onNavigate)
        AdminRoute.BulkSignup -> AdminBulkSignupScreen()
        AdminRoute.Clients -> AdminClientsScreen()
        AdminRoute.Workers -> AdminWorkersScreen()
        AdminRoute.Services -> AdminServicesScreen()
        AdminRoute.MembershipPlans -> AdminMembershipPlansScreen()
        AdminRoute.MembershipOrders -> AdminMembershipOrdersScreen()
        AdminRoute.Memberships -> AdminMembershipsScreen(
            canMembershipOverride = user.membershipOverride,
        )
        AdminRoute.ClientAccounts -> AdminClientAccountsScreen()
        AdminRoute.Orders -> AdminOrdersScreen()
        AdminRoute.Payments -> AdminPaymentsScreen()
        AdminRoute.Marketing -> AdminMarketingScreen()
        AdminRoute.Products -> AdminProductsScreen()
        AdminRoute.StoreSales -> AdminStoreSalesScreen()
        AdminRoute.Schedules -> AdminSchedulesScreen(permissions = user.permissions)
        AdminRoute.Bookings -> AdminBookingsScreen(permissions = user.permissions)
        AdminRoute.Profile -> AdminProfileScreen(
            user = user,
            onUserUpdated = onUserUpdated,
        )
        AdminRoute.Reports -> AdminReportsScreen(user = user)
        AdminRoute.TermsMonitoring -> AdminTermsMonitoringPanel()
        AdminRoute.Settings -> AdminSettingsScreen(
            user = user,
            pendingApprovalsCount = pendingApprovalsCount,
            openApprovalsTab = openSettingsApprovals,
        )
    }
}
